import React, { useLayoutEffect, useMemo, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { createPortal } from 'react-dom';

import styles from './OrganizationDropdown.module.css';

const MIN_PANEL_WIDTH = 56;

const computeCoords = (anchorEl, measuredWidth) => {
  const rect = anchorEl.getBoundingClientRect();
  const GAP_X = 8;
  const viewportWidth = window.innerWidth || document.documentElement.clientWidth || 0;
  const maxAllowedWidth = Math.max(MIN_PANEL_WIDTH, viewportWidth - 16);
  const natural = typeof measuredWidth === 'number' && measuredWidth > 0 ? measuredWidth : MIN_PANEL_WIDTH;
  const width = Math.min(Math.max(natural, MIN_PANEL_WIDTH), maxAllowedWidth);
  let left = rect.right + GAP_X;
  if (left + width > viewportWidth - 8) {
    left = Math.max(8, viewportWidth - width - 8);
  }
  return { top: rect.top, left };
};

export const SubDropdown = ({ children, className, onMouseEnter, onMouseLeave, anchorRef, portalContainerRef }) => {
  const [coords, setCoords] = useState(null);
  const panelRef = useRef(null);

  useLayoutEffect(() => {
    if (!anchorRef?.current || typeof window === 'undefined') return;

    const anchorEl = anchorRef.current;

    const update = () => {
      if (!anchorRef?.current) return;
      const el = panelRef.current;
      const measuredWidth = el ? el.getBoundingClientRect().width : 0;
      setCoords(computeCoords(anchorRef.current, measuredWidth));
    };

    update();
    // Second pass: first layout may not include final font/checkbox metrics.
    const raf = window.requestAnimationFrame(() => update());
    window.addEventListener('scroll', update, true);
    window.addEventListener('resize', update);
    return () => {
      window.cancelAnimationFrame(raf);
      window.removeEventListener('scroll', update, true);
      window.removeEventListener('resize', update);
    };
  }, [anchorRef, children]);

  const style = useMemo(
    () =>
      coords
        ? {
            top: coords.top,
            left: coords.left,
            // Do not set width inline — let the panel shrink to content; `width` is only used for `left` math above.
          }
        : { visibility: 'hidden' },
    [coords],
  );

  if (typeof document === 'undefined') {
    return null;
  }

  const content = (
    <div
      ref={panelRef}
      className={[styles.subDropdown, className].filter(Boolean).join(' ')}
      data-org-filter-sub-panel
      role='presentation'
      onMouseDown={(e) => e.stopPropagation()}
      onClick={(e) => e.stopPropagation()}
      onMouseEnter={onMouseEnter}
      onMouseLeave={onMouseLeave}
      style={style}>
      {children}
    </div>
  );

  if (!anchorRef?.current) {
    return null;
  }

  // Portal into the parent row wrapper when provided so the panel stays under the same
  // DOM ancestor as the trigger row (avoids rc-trigger “click outside” closing the filter
  // popover, and avoids parentWrap mouseLeave firing when moving into the submenu).
  const mountNode =
    portalContainerRef?.current ||
    anchorRef.current.closest('[data-nested-checkbox-filter-root]') ||
    anchorRef.current.closest('.ant-popover-inner-content') ||
    anchorRef.current.closest('.ant-modal-body') ||
    anchorRef.current.closest('.ant-drawer-body') ||
    document.body;

  return createPortal(content, mountNode);
};

SubDropdown.propTypes = {
  children: PropTypes.node.isRequired,
  className: PropTypes.string,
  onMouseEnter: PropTypes.func,
  onMouseLeave: PropTypes.func,
  anchorRef: PropTypes.shape({
    current: PropTypes.instanceOf(Element),
  }),
  portalContainerRef: PropTypes.shape({
    current: PropTypes.instanceOf(Element),
  }),
};
